import React, { createContext, useContext, useEffect, useMemo } from 'react';

/**
 * ThemeContext — DISABLED (locked to light mode).
 *
 * The multi-mode theme system (system/light/dark) was previously exposed via
 * a sidebar toggle, but users reported that dark-mode preferences drifted the
 * app palette from the original brand look. Until we ship a designer-approved
 * dark palette, this provider now:
 *   • Always renders in LIGHT mode.
 *   • Actively strips any leftover `.dark` class on <html>.
 *   • Clears any stale `uppower_theme_mode` value in localStorage so a user
 *     who previously picked "dark" doesn't stay stuck on it.
 *   • Keeps `useTheme()` compatible so existing imports don't break.
 */

const STORAGE_KEY = 'uppower_theme_mode';
const NAVY = '#0B1E3F';
const ThemeCtx = createContext(null);

async function forceLightStatusBar() {
    try {
        if (typeof window === 'undefined' || !window.Capacitor) return;
        const platform = window.Capacitor.getPlatform && window.Capacitor.getPlatform();
        if (!platform || platform === 'web') return;
        const mod = await import('@capacitor/status-bar');
        const { StatusBar, Style } = mod || {};
        if (!StatusBar) return;
        // Light content on top of navy → white icons stay legible.
        await StatusBar.setStyle({ style: Style.Light });
        await StatusBar.setBackgroundColor({ color: NAVY });
    } catch { /* silent — status bar plugin unavailable on web */ }
}

function forceLightThemeOnce() {
    try {
        const root = document.documentElement;
        root.classList.remove('dark');
        // Clear any prior explicit choice so the app stays light on next launch.
        try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
        // Sync PWA / browser chrome theme colour.
        let meta = document.querySelector('meta[name="theme-color"]');
        if (!meta) {
            meta = document.createElement('meta');
            meta.setAttribute('name', 'theme-color');
            document.head.appendChild(meta);
        }
        meta.setAttribute('content', NAVY);
    } catch { /* ignore */ }
}

export function ThemeProvider({ children }) {
    useEffect(() => {
        forceLightThemeOnce();
        forceLightStatusBar();
    }, []);

    const value = useMemo(() => ({
        mode: 'light',
        resolved: 'light',
        setMode: () => { /* locked — no-op */ },
        cycle: () => { /* locked — no-op */ },
    }), []);

    return <ThemeCtx.Provider value={value}>{children}</ThemeCtx.Provider>;
}

export function useTheme() {
    return useContext(ThemeCtx) || { mode: 'light', resolved: 'light', setMode: () => {}, cycle: () => {} };
}
